// A compact checkbox dropdown for the filter bar: shows the label + a count
// badge when anything is selected, opens a popover of options with their
// facet counts, and a type-to-narrow box when the list is long (profiles,
// models, owners). Controlled; the page owns the selection.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import styles from './MultiSelect.module.css';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Facet count under the current OTHER filters; undefined = unknown. */
  count?: number;
  /** Optional secondary line (e.g. an owner's email). */
  hint?: string;
}

export interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear?: () => void;
  /** Show the narrow box once the list is at least this long (default 8). */
  searchThreshold?: number;
  /** Render a swatch/badge for an option (stream colours). */
  renderOption?: (opt: MultiSelectOption) => React.ReactNode;
  testId?: string;
}

export function MultiSelect({
  label,
  options,
  selected,
  onToggle,
  onClear,
  searchThreshold = 8,
  renderOption,
  testId,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [needle, setNeedle] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Close on outside click / Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Selected values that the current facets no longer list (they were picked
  // under a different filter set) still need to be visible and un-pickable.
  const merged = useMemo(() => {
    const known = new Set(options.map((o) => o.value));
    const orphans = selected
      .filter((v) => !known.has(v))
      .map<MultiSelectOption>((v) => ({ value: v, label: v || '(untagged)', count: 0 }));
    return [...options, ...orphans];
  }, [options, selected]);

  const visible = useMemo(() => {
    const n = needle.trim().toLowerCase();
    if (!n) return merged;
    return merged.filter(
      (o) =>
        o.label.toLowerCase().includes(n) ||
        o.value.toLowerCase().includes(n) ||
        (o.hint ?? '').toLowerCase().includes(n),
    );
  }, [merged, needle]);

  const showSearch = merged.length >= searchThreshold;

  return (
    <div className={styles.root} ref={rootRef} data-testid={testId}>
      <button
        type="button"
        className={`${styles.trigger} ${selected.length ? styles.triggerActive : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        {selected.length > 0 && <span className={styles.count}>{selected.length}</span>}
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className={styles.pop} role="listbox" id={listId} aria-multiselectable="true">
          {showSearch && (
            <input
              className={styles.search}
              placeholder={`Find ${label.toLowerCase()}…`}
              value={needle}
              onChange={(e) => setNeedle(e.target.value)}
              autoFocus
            />
          )}
          <div className={styles.list}>
            {visible.length === 0 && <div className={styles.empty}>No matches</div>}
            {visible.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`${styles.opt} ${checked ? styles.optOn : ''}`}
                  role="option"
                  aria-selected={checked}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(opt.value)}
                  />
                  <span className={styles.optBody}>
                    <span className={styles.optLabel}>
                      {renderOption ? renderOption(opt) : opt.label}
                    </span>
                    {opt.hint && <span className={styles.optHint}>{opt.hint}</span>}
                  </span>
                  {opt.count !== undefined && (
                    <span className={styles.optCount}>{opt.count.toLocaleString()}</span>
                  )}
                </label>
              );
            })}
          </div>
          {selected.length > 0 && onClear && (
            <button type="button" className={styles.clear} onClick={onClear}>
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
